import axios from 'axios';
import {
    TrustShieldBankAccount,
    TrustShieldExportRecord,
    DepositExportResult,
    TrustLedgerDiffPayload,
} from './trustShieldTypes';

/** Extracts a user-friendly error message from a TrustShield API error. */
export function extractTrustShieldError(err: unknown, fallback = 'An error occurred'): string {
    if (err && typeof err === 'object' && 'response' in err) {
        const data = (err as any).response?.data;
        if (data?.error === 'AccountDeactivated') {
            return 'Your account has been deactivated. Please contact your administrator to reactivate it.';
        }
    }
    return (err as any)?.message || fallback;
}

export async function fetchTrustAccounts(jwt: string): Promise<TrustShieldBankAccount[]> {
    const response = await axios.get('/ts/api/bank-accounts?status=ACTIVE', {
        headers: { Authorization: `Bearer ${jwt}` },
    });
    return response.data.data.trust_accounts;
}

export async function fetchExports(
    jwt: string,
    appId: number
): Promise<TrustShieldExportRecord[]> {
    const response = await axios.get(
        `/ts/api/realti/exports?appId=${appId}`,
        { headers: { Authorization: `Bearer ${jwt}` } }
    );
    return response.data.data.items;
}

export interface MatterSearchResult {
    id: string;
    file_number: string;
    display: string;
    client_name?: string;
    default_trust_account_id?: string;
}

export async function fetchMatterByAppId(
    jwt: string,
    appId: number
): Promise<MatterSearchResult | null> {
    try {
        const response = await axios.get(
            `/ts/api/transactions/matters/by-app/${appId}`,
            { headers: { Authorization: `Bearer ${jwt}` } }
        );
        return response.data?.data?.matter ?? null;
    } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 404) return null;
        throw err;
    }
}

export async function fetchMatterDefaultAccount(
    jwt: string,
    appId: number
): Promise<string | null> {
    const matter = await fetchMatterByAppId(jwt, appId);
    return matter?.default_trust_account_id ?? null;
}

export async function submitDeposits(
    jwt: string,
    appId: number,
    fileNumber: string,
    clientName: string,
    fileCreationDate: string,
    bankAccountId: string,
    items: Array<{
        hash: string;
        amountCents: number;
        description: string;
        payee_payor: string;
        bank_account_id?: string;
        payment_method?: string;
        reference_number?: string;
        already_deposited?: boolean;
        transaction_date?: string;
    }>
): Promise<DepositExportResult[]> {
    const response = await axios.post(
        '/ts/api/realti/exports/deposits',
        { appId, fileNumber, clientName, fileCreationDate, bank_account_id: bankAccountId, items },
        { headers: { Authorization: `Bearer ${jwt}` } }
    );
    return response.data.data.results;
}

export interface ProvisionMatterPayload {
    appId: number;
    fileNumber: string;
    clientName: string;
    fileCreationDate: string;
    institution_number?: string;
    transit_number?: string;
    account_number?: string;
}

export interface ProvisionMatterResult {
    matter_id: string;
    file_number: string;
    created: boolean;
}

export async function provisionMatter(
    jwt: string,
    payload: ProvisionMatterPayload
): Promise<ProvisionMatterResult> {
    const response = await axios.post(
        '/api/realti/matters/provision',
        payload,
        { headers: { Authorization: `Bearer ${jwt}` } }
    );
    return response.data.data;
}

export async function submitSingleDeposit(
    jwt: string,
    payload: {
        appId: number;
        fileNumber: string;
        clientName: string;
        fileCreationDate: string;
        item: {
            hash: string;
            amountCents: number;
            description: string;
            payee_payor: string;
            bank_account_id: string;
            payment_method: string;
            reference_number?: string;
            already_deposited?: boolean;
        };
    }
): Promise<DepositExportResult> {
    const results = await submitDeposits(
        jwt,
        payload.appId,
        payload.fileNumber,
        payload.clientName,
        payload.fileCreationDate,
        payload.item.bank_account_id,
        [{
            hash: payload.item.hash,
            amountCents: payload.item.amountCents,
            description: payload.item.description,
            payee_payor: payload.item.payee_payor,
            bank_account_id: payload.item.bank_account_id,
            payment_method: payload.item.payment_method,
            reference_number: payload.item.reference_number,
            already_deposited: payload.item.already_deposited,
        }]
    );
    return results[0];
}

/**
 * Fetch the matter's trust-ledger transactions that have NO Realti linkage
 * (created directly in TrustShield). Powers the "entries not recorded in
 * Realti" alert. A 404 means the endpoint isn't deployed yet — degrade to
 * empty so nothing is surfaced (the feature lights up once the trust API ships).
 */
export async function fetchUnlinkedTransactions(
    jwt: string,
    appId: number
): Promise<TrustLedgerDiffPayload> {
    try {
        const response = await axios.get(
            `/ts/api/realti/matters/${appId}/unlinked-transactions`,
            { headers: { Authorization: `Bearer ${jwt}` } }
        );
        const data = response.data?.data ?? {};
        return { receipts: data.receipts ?? [], payments: data.payments ?? [] };
    } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 404) {
            return { receipts: [], payments: [] };
        }
        throw err;
    }
}
